import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Shield, Eye, Ear, Wind, Heart, Sparkles, ArrowRight } from "lucide-react";
import { UniversalSymptomTracker } from '@/components/mental/UniversalSymptomTracker';
import { useUniversalTracking } from '@/hooks/useUniversalTracking';

interface SafePlaceVisualizationProps {
  onBack?: () => void;
  onComplete?: () => void;
}

type ExerciseStep = 'intro' | 'symptoms-before' | 'visualization' | 'symptoms-after' | 'complete';

export const SafePlaceVisualization = ({ onBack, onComplete }: SafePlaceVisualizationProps) => {
  const [exerciseStep, setExerciseStep] = useState<ExerciseStep>('intro');
  const [visualizationStep, setVisualizationStep] = useState(0);
  const [responses, setResponses] = useState<Record<number, string>>({});
  const [symptomsBefore, setSymptomsBefore] = useState<Record<string, number>>({});
  const [symptomsAfter, setSymptomsAfter] = useState<Record<string, number>>({});
  const { logExercise, isLogging } = useUniversalTracking();

  const steps = [
    {
      title: 'Choose Your Safe Place',
      icon: Shield,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      prompt: 'Where is your safe place? It can be real or imaginary.',
      guidance: 'This could be a beach, a forest, a cozy room, or anywhere you feel completely safe and at peace. There is no wrong answer - this is YOUR space.',
      examples: ['A quiet beach at sunset', 'A forest clearing', 'My grandmother\'s kitchen', 'A mountain cabin', 'A starlit meadow']
    },
    {
      title: 'Visual Details',
      icon: Eye,
      color: 'text-soft-accent',
      bgColor: 'bg-soft-accent/10',
      prompt: 'What do you see in your safe place?',
      guidance: 'Notice colors, lighting, objects, and any movement. Make it vivid and real in your mind.',
      examples: ['Warm golden light', 'Gentle waves', 'Comfortable furniture', 'Flowers blooming', 'Birds flying']
    },
    {
      title: 'Sounds',
      icon: Ear,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      prompt: 'What sounds do you hear?',
      guidance: 'Listen to the soothing sounds around you. They add depth to your safe space.',
      examples: ['Ocean waves', 'Wind in trees', 'Crackling fire', 'Soft music', 'Birds singing']
    },
    {
      title: 'Physical Sensations',
      icon: Wind,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      prompt: 'What do you feel physically?',
      guidance: 'Notice temperature, textures, and any physical sensations of comfort and safety.',
      examples: ['Warm sun on skin', 'Soft blanket', 'Cool breeze', 'Comfortable chair', 'Grass beneath feet']
    },
    {
      title: 'Emotional Safety',
      icon: Heart,
      color: 'text-soft-accent',
      bgColor: 'bg-soft-accent/10',
      prompt: 'How does it feel to be in this place?',
      guidance: 'Connect with the sense of safety, peace, and comfort this place brings you.',
      examples: ['Completely safe', 'Deeply peaceful', 'Free and light', 'Protected', 'At home']
    }
  ];

  const currentStep = steps[visualizationStep];
  const Icon = currentStep?.icon || Shield;

  const handleComplete = async () => {
    const success = await logExercise({
      exercise_type: 'safe_place_visualization',
      exercise_name: 'Safe Place Visualisation',
      category: 'grounding',
      duration_minutes: 10,
      completion_data: {
        safe_place: responses[0],
        visual_details: responses[1],
        sounds: responses[2],
        physical_sensations: responses[3],
        emotional_safety: responses[4]
      },
      symptoms_before: symptomsBefore,
      symptoms_after: symptomsAfter,
    });

    if (success) {
      setExerciseStep('complete');
    }
  };

  // Intro step
  if (exerciseStep === 'intro') {
    return (
      <div className="space-y-6 animate-fade-in">
        {onBack && (
          <Button variant="outline" onClick={onBack}>← Back</Button>
        )}
        <Card className="bg-gradient-therapeutic border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl font-light">Safe Place Visualisation</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Create a mental sanctuary you can return to anytime
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              This exercise helps you create a detailed mental safe space that activates your 
              relaxation response. You'll build it step by step, engaging all your senses.
            </p>
            <Button onClick={() => setExerciseStep('symptoms-before')} className="w-full gap-2">
              Begin Exercise <ArrowRight className="w-4 h-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Symptoms before
  if (exerciseStep === 'symptoms-before') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Button variant="outline" onClick={() => setExerciseStep('intro')}>← Back</Button>
        <UniversalSymptomTracker
          mode="before"
          onRatingsChange={setSymptomsBefore}
        />
        <Button 
          onClick={() => setExerciseStep('visualization')} 
          className="w-full"
          disabled={Object.keys(symptomsBefore).length === 0}
        >
          Continue to Exercise
        </Button>
      </div>
    );
  }

  // Symptoms after
  if (exerciseStep === 'symptoms-after') {
    return (
      <div className="space-y-6 animate-fade-in">
        <UniversalSymptomTracker
          mode="after"
          previousRatings={symptomsBefore}
          onRatingsChange={setSymptomsAfter}
        />
        <Button 
          onClick={handleComplete} 
          className="w-full"
          disabled={Object.keys(symptomsAfter).length === 0 || isLogging}
        >
          {isLogging ? 'Saving...' : 'Complete Exercise'}
        </Button>
      </div>
    );
  }

  // Complete
  if (exerciseStep === 'complete') {
    return (
      <div className="space-y-6 animate-fade-in">
        <Card className="bg-gradient-to-br from-accent/20 to-primary/10 border-accent/30">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-accent/20 flex items-center justify-center mx-auto">
              <Sparkles className="w-8 h-8 text-accent" />
            </div>
            <h3 className="text-xl font-semibold">Safe Place Created!</h3>
            <p className="text-muted-foreground">
              You've built a powerful mental sanctuary. Practice visiting this place 
              daily to strengthen the neural pathways and make it easier to access 
              during times of stress.
            </p>
            <Button onClick={onComplete || onBack} className="w-full">
              Finish
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main visualisation steps
  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-full bg-primary/10">
                <Shield className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl font-light">Safe Place Visualisation</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Create a mental sanctuary you can return to anytime
                </p>
              </div>
            </div>
            <Badge variant="secondary">Step {visualizationStep + 1}/{steps.length}</Badge>
          </div>
        </CardHeader>
      </Card>

      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-3 rounded-full ${currentStep.bgColor}`}>
              <Icon className={`h-6 w-6 ${currentStep.color}`} />
            </div>
            <CardTitle className="text-xl font-light">{currentStep.title}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="bg-gradient-calm rounded-lg p-4 space-y-2">
            <p className="font-medium">{currentStep.prompt}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{currentStep.guidance}</p>
          </div>

          <div className="space-y-3">
            <p className="text-sm font-medium text-muted-foreground">Ideas to inspire you:</p>
            <div className="grid gap-2">
              {currentStep.examples.map((example, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-2 text-sm bg-background/50 rounded-lg px-3 py-2 border border-border/30"
                >
                  <Sparkles className="h-3 w-3 text-primary flex-shrink-0" />
                  <span>{example}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Your response:</label>
            <Textarea
              value={responses[visualizationStep] || ''}
              onChange={(e) => setResponses({ ...responses, [visualizationStep]: e.target.value })}
              placeholder="Take your time... describe what comes to mind"
              className="min-h-[120px] bg-background/80"
            />
          </div>

          <div className="flex gap-3">
            {visualizationStep > 0 && (
              <Button variant="outline" onClick={() => setVisualizationStep(visualizationStep - 1)} className="flex-1">
                Previous
              </Button>
            )}
            {visualizationStep < steps.length - 1 ? (
              <Button 
                onClick={() => setVisualizationStep(visualizationStep + 1)} 
                className="flex-1"
                disabled={!responses[visualizationStep]?.trim()}
              >
                Next Step
              </Button>
            ) : (
              <Button 
                onClick={() => setExerciseStep('symptoms-after')} 
                className="flex-1"
                disabled={!responses[visualizationStep]?.trim()}
              >
                Rate How You Feel Now
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="py-4">
          <div className="space-y-2">
            <p className="text-sm font-medium">Why This Works</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Safe place visualisation is an evidence-based technique used in trauma therapy. By creating a detailed 
              mental sanctuary, you give your mind a place of refuge during distress. Your brain responds to vivid 
              imagination similarly to real experiences, activating your relaxation response.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
